import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

let activeRequest = null;

class CartCreditPreview extends HTMLElement {
  storageExpiryKey = 'aaronya_cart_credit_expires_at_v3';
  storageExpiredKey = 'aaronya_cart_credit_expired_v3';
  static observedAttributes = ['data-cart-total', 'data-has-other-discount', 'data-current-preview-code', 'hidden'];
  failedRequest = '';

  attributeChangedCallback() {
    this.scheduleRefresh();
  }

  intervalId = null;
  refreshFrame = null;
  drawerOpen = false;
  fallbackExpiry = null;
  fallbackExpired = false;

  connectedCallback() {
    this.drawer = this.closest('theme-drawer');
    this.summary = this.closest('.cart-drawer__summary');
    this.drawer?.addEventListener('theme-drawer:open', this.handleDrawerOpen);
    this.drawer?.addEventListener('theme-drawer:close', this.handleDrawerClose);
    this.resetPreviewFromUrl();

    if (this.drawer?.hasAttribute('open')) this.handleDrawerOpen();
  }

  disconnectedCallback() {
    this.drawer?.removeEventListener('theme-drawer:open', this.handleDrawerOpen);
    this.drawer?.removeEventListener('theme-drawer:close', this.handleDrawerClose);
    this.stopTimer();
    if (this.refreshFrame !== null) window.cancelAnimationFrame(this.refreshFrame);
    this.refreshFrame = null;
    this.drawerOpen = false;
  }

  handleDrawerOpen = () => {
    this.drawerOpen = true;
    this.scheduleRefresh();
  };

  handleDrawerClose = () => {
    this.drawerOpen = false;
    this.stopTimer();
  };

  scheduleRefresh() {
    if (!this.isConnected || !this.drawerOpen) return;
    if (this.refreshFrame !== null) window.cancelAnimationFrame(this.refreshFrame);

    this.refreshFrame = window.requestAnimationFrame(() => {
      this.refreshFrame = null;
      this.refresh();
    });
  }

  resetPreviewFromUrl() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('cart_credit_preview') !== 'reset') return;

    this.removeStoredValue(this.storageExpiryKey);
    this.removeStoredValue(this.storageExpiredKey);
    this.fallbackExpiry = null;
    this.fallbackExpired = false;
    url.searchParams.delete('cart_credit_preview');
    window.history.replaceState({}, '', url);
  }

  refresh() {
    const cartTotal = Number(this.dataset.cartTotal || 0);
    const lowThreshold = Number(this.dataset.thresholdLow || 10001);
    const highThreshold = Number(this.dataset.thresholdHigh || 10001);
    const hasOtherDiscount = this.dataset.hasOtherDiscount === 'true';

    if (hasOtherDiscount || cartTotal < lowThreshold || this.isExpired()) {
      this.hide();
      if (!hasOtherDiscount) this.syncDiscount('');
      return;
    }

    const isHighTier = cartTotal >= highThreshold;
    const amount = isHighTier ? this.dataset.creditHigh : this.dataset.creditLow;
    const desiredCode = isHighTier ? this.dataset.codeHigh : this.dataset.codeLow;
    const amountElement = this.querySelector('[data-credit-amount]');
    if (amountElement) amountElement.textContent = amount || '';

    let expiresAt = Number(this.getStoredValue(this.storageExpiryKey) || this.fallbackExpiry || 0);
    if (!expiresAt) {
      expiresAt = Date.now() + Number(this.dataset.duration || 1800000);
      this.fallbackExpiry = expiresAt;
      this.setStoredValue(this.storageExpiryKey, String(expiresAt));
    }

    if (expiresAt <= Date.now()) {
      this.expire();
      return;
    }

    const requestKey = `${desiredCode}:${cartTotal}`;
    if (this.failedRequest === requestKey) {
      this.hide();
      return;
    }

    if (this.hidden) this.hidden = false;
    this.startTimer(expiresAt);
    this.syncDiscount(desiredCode || '');
  }

  async syncDiscount(desiredCode) {
    const currentCode = this.dataset.currentPreviewCode || '';
    const sectionId = this.dataset.sectionId;
    if (!sectionId || currentCode === desiredCode || activeRequest) return;

    const deferredPromise = CartDiscountUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: desiredCode ? [{ code: desiredCode }] : [],
        promise: deferredPromise.promise,
      })
    );

    this.summary?.setAttribute('data-credit-updating', 'true');
    const abortController = new AbortController();
    activeRequest = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: desiredCode,
          sections: [sectionId],
        }),
      });
      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
      if (!response.ok) throw new Error('Unable to apply the private art credit');
      const data = await response.json();
      const applied = !desiredCode || data.discount_codes?.some((discount) => discount.code.toUpperCase() === desiredCode.toUpperCase() && discount.applicable);

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      if (!applied) {
        this.failedRequest = `${desiredCode}:${this.dataset.cartTotal}`;
        this.hide();
        return;
      }

      this.failedRequest = '';
      this.dataset.currentPreviewCode = desiredCode;
      const newHtml = data.sections?.[sectionId];
      if (newHtml) {
        morphSection(sectionId, newHtml, {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
      }
    } catch (error) {
      this.failedRequest = `${desiredCode}:${this.dataset.cartTotal}`;
      this.hide();
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Unable to apply the private art credit',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      activeRequest = null;
      this.summary?.removeAttribute('data-credit-updating');
      document.querySelectorAll('cart-credit-preview').forEach((credit) => credit.scheduleRefresh());
    }
  }

  startTimer(expiresAt) {
    this.stopTimer();
    this.updateTimer(expiresAt);
    this.intervalId = window.setInterval(() => this.updateTimer(expiresAt), 1000);
  }

  stopTimer() {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  updateTimer(expiresAt) {
    const remaining = Math.max(0, expiresAt - Date.now());
    if (remaining === 0) {
      this.expire();
      return;
    }

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    const timer = this.querySelector('[data-credit-timer]');
    if (timer) timer.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  expire() {
    this.fallbackExpired = true;
    this.setStoredValue(this.storageExpiredKey, 'true');
    this.removeStoredValue(this.storageExpiryKey);
    this.hide();
    this.syncDiscount('');
  }

  isExpired() {
    return this.fallbackExpired || this.getStoredValue(this.storageExpiredKey) === 'true';
  }

  hide() {
    if (!this.hidden) this.hidden = true;
    this.stopTimer();
  }

  getStoredValue(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  setStoredValue(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      return;
    }
  }

  removeStoredValue(key) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      return;
    }
  }
}

if (!customElements.get('cart-credit-preview')) {
  customElements.define('cart-credit-preview', CartCreditPreview);
}
